import React, { useEffect, useState, useRef } from "react";
import { SkipForward, ExternalLink } from "lucide-react";
import api from "../lib/api";

/**
 * Preroll ad component. Fetches /ads/config and, if ads_enabled and a preroll
 * campaign exists, plays it in a fullscreen overlay before the video.
 *
 * Supports 1st-party video ads (campaign.video_url) AND 3rd-party tag injection
 * (config.third_party_tag rendered as raw HTML into an isolated div).
 */
export default function AdRoll({ onComplete, enabled = true }) {
  const [ad, setAd] = useState(null);
  const [thirdPartyTag, setThirdPartyTag] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const videoRef = useRef(null);
  const impressionLogged = useRef(false);

  useEffect(() => {
    if (!enabled) { onComplete?.(); return; }
    api.get("/ads/config").then(({ data }) => {
      if (!data.ads_enabled) { onComplete?.(); return; }
      if (data.third_party_tag) {
        setThirdPartyTag(data.third_party_tag);
        // Auto-dismiss third-party ads after 12s so we don't block content
        setTimeout(() => onComplete?.(), 12000);
        return;
      }
      if (data.preroll && data.preroll.video_url) {
        setAd(data.preroll);
        setRemaining(data.preroll.duration_sec || 15);
      } else {
        onComplete?.();
      }
    }).catch(() => onComplete?.());
    // eslint-disable-next-line
  }, [enabled]);

  useEffect(() => {
    if (!ad) return;
    if (!impressionLogged.current) {
      api.post(`/ads/impression/${ad.id}`).catch(() => {});
      impressionLogged.current = true;
    }
    const h = setInterval(() => {
      setRemaining((r) => {
        const next = r - 1;
        if (ad.skippable_after_sec != null && ad.duration_sec - next >= ad.skippable_after_sec) setCanSkip(true);
        if (next <= 0) { clearInterval(h); onComplete?.(); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(h);
    // eslint-disable-next-line
  }, [ad]);

  if (thirdPartyTag) {
    return (
      <div data-testid="adroll-3p" className="absolute inset-0 bg-black z-10 flex items-center justify-center">
        <div className="max-w-full max-h-full overflow-hidden" dangerouslySetInnerHTML={{ __html: thirdPartyTag }} />
        <button
          onClick={onComplete}
          className="absolute top-3 right-3 bg-black/80 text-white text-xs px-3 py-1.5 rounded-full border border-white/20"
          data-testid="adroll-3p-skip"
        >
          Skip ad
        </button>
      </div>
    );
  }

  if (!ad) return null;

  const clickThrough = () => {
    if (!ad.click_through_url) return;
    api.post(`/ads/click/${ad.id}`).catch(() => {});
    window.open(ad.click_through_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="adroll-video" className="absolute inset-0 bg-black z-10">
      <video
        ref={videoRef}
        src={ad.video_url}
        poster={ad.thumbnail_url}
        autoPlay
        className="w-full h-full object-contain"
        onEnded={() => onComplete?.()}
      />
      <div className="absolute top-3 left-3 bg-black/70 border border-white/20 text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full">
        Ad · {remaining}s
      </div>
      {ad.click_through_url && (
        <button
          data-testid="adroll-cta"
          onClick={clickThrough}
          className="absolute bottom-4 left-4 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Learn more
        </button>
      )}
      {canSkip && (
        <button
          data-testid="adroll-skip"
          onClick={onComplete}
          className="absolute bottom-4 right-4 bg-black/80 border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-full flex items-center gap-1.5"
        >
          Skip ad <SkipForward className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
